import { isEmpty, set, uniq } from "lodash";

import { usersFilter } from "components/filterForms/UsersFilterForm/usersFilter";
import { type AssignmentPeopleContext, PriorityLevels, RolePermissions, SortingDirection } from "../enums";
import ItemTypes from "../enums/packItemsTypes";
import {
  ContentType,
  type EntityToPeopleAssignments,
  type FlowPayload,
} from "../features/Library/PeopleAssignments/types";
import { type FlowAssignment } from "../features/Licensing/ContentAssignmentModalLicensingSteps/state/slices/packsContextSlice";
import { type FlowPriorityItem, type GetUsersListAction, type PriorityItem } from "../features/People/types";
import { type PacksContextItemModel } from "../interfaces";
import { type FiltersMap } from "./filterUtils";
import nameof from "./nameof";

const userListUtils = {
  formatOrderParams: (sortingColumnName: string, sortingDirection: SortingDirection) => {
    const paramMap: { [key: string]: string } = {
      name: `firstName ${sortingDirection},lastName ${sortingDirection}`,
      added: `id ${sortingDirection}`,
      "last login": `lastLoginDate ${sortingDirection}`,
      "job title": `title ${sortingDirection}`,
    };

    const name = sortingColumnName.toLowerCase();

    return paramMap[name] || `${name} ${sortingDirection}`;
  },
  formatOrderParamsV2: (sortingColumnName: string, sortingDirection: SortingDirection): string => {
    const paramMap: { [key: string]: string } = {
      name: `firstName ${sortingDirection},lastName ${sortingDirection}`,
      added: `createDate ${sortingDirection}`,
      "no contact": `addedToBlackList ${sortingDirection}`,
      "last login": `lastLoginDate ${sortingDirection}`,
      "job title": `jobTitle ${sortingDirection}`,
    };

    const name = sortingColumnName.toLowerCase();

    return paramMap[name] || `${name} ${sortingDirection}`;
  },
  formatOrderParamsByColumnName: (sortingColumnName: string): string => {
    const paramMap: { [key: string]: string } = {
      name: "firstName",
      "job title": `jobTitle`,
    };

    const name = sortingColumnName.toLowerCase();

    return paramMap[name] || `${name}`;
  },
  formatODataOrderParams: (sortingColumnName: string, sortingDirection: SortingDirection): string[] => {
    return userListUtils.formatOrderParams(sortingColumnName, sortingDirection).split(",");
  },
  mapToPriorityItem: (item: any, contentType: ContentType): PriorityItem => {
    const result = {
      id: item.id,
      title: item.title,
      thumbnailUrl: item.thumbnailUrl,

      daysToComplete: 1,
      priorityLevel: PriorityLevels.none,
      blockType: contentType,
      hasBeenPublished: !!item.hasBeenPublished,
      durationInSeconds: item.durationInSeconds,
    };
    if (contentType === ContentType.Flow) {
      set(result, nameof<FlowPriorityItem>("canAutoStart"), item.canAutoStart);
    }
    return result;
  },
  mapToAssignmentItem: (peopleId: number, item: any, contentType: string, peopleType: AssignmentPeopleContext) => {
    const result: EntityToPeopleAssignments = {
      contentId: item.id,
      peopleId,
      priority: item.priorityLevel ?? 1,
      dueDate: item.priorityLevel === PriorityLevels.required ? item.daysToComplete : null,
      contentType,
      peopleType,
      packIds: item.packIds,
    };
    if (contentType === ContentType.Flow) {
      (result as EntityToPeopleAssignments<FlowPayload>).payload = {
        autoStart: Boolean(item.autoStart),
      };
    }
    return result;
  },
  mapAssignmentFlows(
    peopleId: number,
    flowAssignments: FlowAssignment[],
    priorityItems: FlowPriorityItem[],
    peopleType: AssignmentPeopleContext,
  ): EntityToPeopleAssignments<FlowPayload>[] {
    const assignments = flowAssignments.map((item) => ({
      contentId: item.flowId,
      contentType: ItemTypes.Flow,
      packIds: item.packIds,
      peopleId,
      peopleType,
    }));

    return assignments.map((assignmentItem) => {
      const priorityItem = priorityItems.find((pi) => pi.id === assignmentItem.contentId);
      return {
        ...assignmentItem,
        priority: priorityItem?.priorityLevel ?? PriorityLevels.none,
        dueDate: priorityItem?.priorityLevel === PriorityLevels.required ? priorityItem.daysToComplete : null,
        payload: {
          autoStart: Boolean(priorityItem?.autoStart),
        },
      };
    });
  },
  mapAssignmentItems: (priorityItems: PriorityItem[], packContextItems: PacksContextItemModel[]): PriorityItem[] =>
    priorityItems.map((priorityItem) => {
      let packIds: number[];
      const blockTypeLower = priorityItem.blockType.toLowerCase();
      if (blockTypeLower === ItemTypes.Flow.toLowerCase()) {
        packIds = uniq(packContextItems.filter((p) => p.flowIds.indexOf(priorityItem.id) > -1).map((p) => p.packId!));
      } else {
        packIds = packContextItems
          .filter((p) => p.type.toLowerCase() === blockTypeLower && p.id === priorityItem.id.toString())
          .map((p) => p.packId!);
      }

      return { ...priorityItem, packIds };
    }),
  getUsersFactory: (fetchUsersAction: GetUsersListAction, search: string) => {
    return function (
      skip: number,
      top: number,
      sortingColumnName: string = "",
      sortingDirection: SortingDirection = SortingDirection.Descending,
      filter: FiltersMap = {},
    ) {
      const { orderParams, filterParams } = userListUtils.getFetchUsersParams(
        search,
        sortingColumnName,
        sortingDirection,
        filter,
        () => sortingColumnName !== "last login" && isEmpty(filter.lastLoginDate),
      );
      fetchUsersAction(skip, top, orderParams, filterParams, search);
    };
  },
  getOverviewUsers: (fetchUsersAction: GetUsersListAction, search: string) => {
    return function (
      skip: number,
      top: number,
      sortingColumnName: string = "",
      sortingDirection: SortingDirection = SortingDirection.Descending,
      filter: FiltersMap = {},
    ) {
      const orderParams = userListUtils.formatOrderParamsV2(sortingColumnName, sortingDirection);
      fetchUsersAction(skip, top, orderParams, filter, search);
    };
  },
  getFetchUsersParams: (
    search: string,
    sortingColumn: string = "",
    sortingDirection: SortingDirection = SortingDirection.Descending,
    filter: FiltersMap = {},
    predicate?: () => boolean,
  ) => {
    const getParamsV1 = () => ({
      orderParams: userListUtils.formatOrderParams(sortingColumn, sortingDirection),
      filterParams: usersFilter.buildFilterQuery(filter),
    });

    const getParamsV2 = () => ({
      orderParams: userListUtils.formatOrderParamsV2(sortingColumn, sortingDirection),
      filterParams: filter,
    });

    return isEmpty(search) && (!predicate || predicate()) ? getParamsV1() : getParamsV2();
  },
  mapToContentUsers: <T extends { id?: number; userId?: number }>(userList: T[]) =>
    userList.map((x) => ({ ...x, id: x.id ?? x.userId })),

  getCanEditContent: (
    userContent: Record<
      ContentType,
      {
        ids: number[];
        undeletableIds: number[];
      }
    >,
    userPermissions: RolePermissions[],
  ) => {
    return (
      (!userContent.flow.ids.length || userPermissions.includes(RolePermissions.FlowsView)) &&
      ((!userContent.video.ids.length &&
        !userContent.assessment.ids.length &&
        !userContent.survey.ids.length &&
        !userContent.pdf.ids.length) ||
        userPermissions.includes(RolePermissions.AssetsView)) &&
      (!userContent.externalEvent.ids.length || userPermissions.includes(RolePermissions.EventsView))
    );
  },
};

export default userListUtils;
